import { Shield, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmDelete } from '@/components/ConfirmDelete';
import { toast } from '@/hooks/use-toast';
import { useDeleteRole } from '@/hooksApi';
import { Role } from '@/types';

interface RoleRowProps {
  role: Role;
  onEdit: (role: Role) => void;
  isAdminRole: (role: Role) => boolean;
}

export const RoleRow = ({ role, onEdit, isAdminRole }: RoleRowProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: deleteRole, isLoading: isDeleting } = useDeleteRole();

  const handleDelete = () => {
    if (isDeleting) return;
    deleteRole(role.uid, {
      onSuccess: () => {
        queryClient.invalidateQueries(['teamMember-roles']);
        queryClient.invalidateQueries(['teamMember-users']);
        toast({ title: t('role_removed') });
      },
    });
  };

  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
      <div className='p-5 flex items-start gap-4'>
        <div className='h-10 w-10 rounded-lg bg-sd-muted flex items-center justify-center shrink-0'>
          <Shield className='h-5 w-5 text-sd-muted-foreground' />
        </div>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2 mb-1'>
            <h3 className='text-sm font-semibold text-sd-foreground m-0'>{role.name}</h3>
            {role.description && (
              <span className='text-sm text-sd-muted-foreground' dir='rtl'>
                {role.description}
              </span>
            )}
          </div>
          <div className='flex flex-wrap gap-1.5 mt-2'>
            {role.permissions?.map((p) => (
              <span
                key={p.uid}
                className='px-2 py-0.5 rounded-md bg-sd-muted text-xs text-sd-muted-foreground'
              >
                {p.name}
              </span>
            ))}
          </div>
        </div>
        {!isAdminRole(role) && (
          <div className='flex items-center gap-1 shrink-0'>
            <button
              onClick={() => onEdit(role)}
              className='p-1.5 rounded-lg text-sd-muted-foreground hover:text-sd-foreground hover:bg-sd-muted transition-colors'
            >
              <Pencil className='h-4 w-4' />
            </button>
            <ConfirmDelete
              title={t('delete_role')}
              description={`"${role.name}" ${t('role_will_be_deleted')}`}
              onConfirm={handleDelete}
              disabled={isDeleting}
            />
          </div>
        )}
      </div>
    </div>
  );
};
